/**
 * nebula_volume — the sector's nebulae as baked volumes (space realism, 2026-10-07).
 *
 * `NebulaController` is the glue SpaceModule installs per sector entry: it takes the system's sites (nebula_look),
 * bakes each one's volume in SLICES on idle frame time (the engine's NebulaBaker — never a stall; what the pilot sees
 * largest is baked first), builds the engine's NebulaVolume for it, compiles it before its first draw (THE WARM) and
 * fades it in. Baked volumes are kept for the session (`volumeCache`), so coming back to a system is instant.
 *
 * SEATING. A nebula is a group at its ABSOLUTE position with the unit-sphere mesh scaled to its radius inside; the
 * group is handed to the sector's far-body anchors, so past 100 km it is drawn at 100 km and scaled down — which the
 * march cannot tell from the real thing: it works in the mesh's local space, where the camera's position is the same.
 * THE BODY LAYER LAW applies to it as to any far body's shell: at the anchor distance the depth buffer cannot order it
 * against a planet's impostor, so a far nebula is a BACKDROP (its depth goes to the far plane — every body covers it);
 * within 100 km it keeps its true depth. Reversed depth keeps the true position.
 */
import * as THREE from 'three/webgpu';
import { Fn, uniform, vec4, float, select, min, positionLocal, modelViewMatrix, cameraProjectionMatrix } from 'three/tsl';
import { NebulaBaker, type NebulaVolumeData } from 'enginex/render/space/nebula/NebulaField.js';
import { NebulaVolume, NEBULA_QUALITY } from 'enginex/render/space/nebula/NebulaVolume.js';
import { DEPTH_CODE, BODY_LAYER_CODES } from './space_look.js';
import { nebulaSpecFor, nebulaBakeOrder, type NebulaSite, type NebulaLevel } from './nebula_look.js';
import { disposeAfterSwap } from '../gpu_dispose.js';

type N = any; // eslint-disable-line @typescript-eslint/no-explicit-any

/** Past this distance the sector draws a body as a scaled impostor (SpaceModule FAR_ANCHOR_M — the anchors' own rule). */
export const NEBULA_FAR_M = 100_000;
/** Bake arithmetic per frame (ms). */
export const NEBULA_BAKE_BUDGET_MS = 5;
/** Seconds a finished nebula takes to fade in. */
export const NEBULA_FADE_S = 2.5;
/** Baked volumes kept for the session (bytes of float32). */
export const NEBULA_CACHE_BYTES = 96 * 1024 * 1024;

const volumeCache = new Map<string, NebulaVolumeData>();
function cacheGet(key: string): NebulaVolumeData | null {
  const v = volumeCache.get(key);
  if (v) { volumeCache.delete(key); volumeCache.set(key, v); }   // most recently used last
  return v ?? null;
}
function cachePut(key: string, v: NebulaVolumeData): void {
  volumeCache.set(key, v);
  let bytes = 0;
  for (const x of volumeCache.values()) bytes += x.data.byteLength;
  for (const k of volumeCache.keys()) { if (bytes <= NEBULA_CACHE_BYTES || k === key) break; bytes -= volumeCache.get(k)!.data.byteLength; volumeCache.delete(k); }
}
/** Bytes of baked volumes held for the session. */
export function nebulaCacheBytes(): number { let b = 0; for (const x of volumeCache.values()) b += x.data.byteLength; return b; }

/** What SpaceModule lends the controller (closures over the sector — no module import here). */
export interface NebulaEnv {
  /** The sector root (render space). */
  parent(): THREE.Object3D | null;
  scene(): THREE.Scene | null;
  renderer(): unknown;
  camera(): THREE.PerspectiveCamera | null;
  heightPx(): number;
  /** The ship's absolute position (metres). */
  shipAbs(): [number, number, number];
  /** Register / release a group with the sector's far-body anchors (it then follows the floating origin). */
  anchor(group: THREE.Group, abs: THREE.Vector3): void;
  unanchor(group: THREE.Group): void;
}

type Stage = 'queued' | 'baking' | 'warming' | 'shown' | 'failed';
interface Entry { site: NebulaSite; stage: Stage; baker: NebulaBaker | null; vol: NebulaVolume | null; group: THREE.Group | null; uFar: { value: number } | null; fade: number; bakeMs: number; cached: boolean; error: string }

export interface NebulaSiteState { key: string; name: string; kind: string; stage: Stage; progress: number; distKm: number; far: boolean; fade: number; bakeMs: number; cached: boolean; steps: number; error: string }
export interface NebulaControllerState { level: NebulaLevel; size: number; sites: NebulaSiteState[]; shown: number; pending: number; cacheBytes: number }

export class NebulaController {
  private entries: Entry[] = [];
  private level: NebulaLevel = 'high';
  private gen = 0;
  private readonly _abs = new THREE.Vector3();

  constructor(private readonly env: NebulaEnv) {}

  /** Take a system's sites (replacing any it had). The work happens in `frame`. */
  build(sites: ReadonlyArray<NebulaSite>, level: NebulaLevel): void {
    this.dispose();
    this.level = level;
    this.entries = nebulaBakeOrder(sites, this.env.shipAbs()).map((site) => ({ site, stage: 'queued' as Stage, baker: null, vol: null, group: null, uFar: null, fade: 0, bakeMs: 0, cached: false, error: '' }));
  }

  /** Per frame: a slice of the bake in hand, the fades, the per-frame uniforms. */
  frame(dt: number): void {
    if (!this.entries.length) return;
    const q = NEBULA_QUALITY[this.level];
    const next = this.entries.find((e) => e.stage === 'baking') ?? this.entries.find((e) => e.stage === 'queued');
    if (next) {
      try {
        const key = `${next.site.kind}:${next.site.seed}:${q.size}`;
        if (next.stage === 'queued') {
          const hit = cacheGet(key);
          if (hit) { next.cached = true; this.raise(next, hit); } else { next.baker = new NebulaBaker(nebulaSpecFor(next.site.kind, next.site.seed), q.size); next.stage = 'baking'; }
        }
        if (next.stage === 'baking' && next.baker!.step(NEBULA_BAKE_BUDGET_MS)) {
          next.bakeMs = next.baker!.spentMs;
          cachePut(key, next.baker!.volume);
          const v = next.baker!.volume;
          next.baker = null;
          this.raise(next, v);
        }
      } catch (e) { next.stage = 'failed'; next.baker = null; next.error = String((e as Error)?.message ?? e); }
    }
    const cam = this.env.camera(), h = this.env.heightPx(), ship = this.env.shipAbs();
    for (const e of this.entries) {
      if (!e.vol || !e.group) continue;
      if (e.stage === 'shown' && e.fade < 1) { e.fade = Math.min(1, e.fade + (Number.isFinite(dt) ? Math.max(0, dt) : 0) / NEBULA_FADE_S); e.vol.setFade(e.fade * e.fade * (3 - 2 * e.fade)); }
      if (cam) e.vol.update(cam, h);
      if (e.uFar) e.uFar.value = Math.hypot(e.site.pos[0] - ship[0], e.site.pos[1] - ship[1], e.site.pos[2] - ship[2]) > NEBULA_FAR_M ? 1 : 0;
    }
  }

  /** A baked volume becomes a seated, compiled, fading-in nebula. */
  private raise(e: Entry, data: NebulaVolumeData): void {
    const parent = this.env.parent();
    if (!parent) { e.stage = 'failed'; e.error = 'no sector'; return; }
    const spec = nebulaSpecFor(e.site.kind, e.site.seed);
    const vol = new NebulaVolume(spec, data, NEBULA_QUALITY[this.level]);
    const uFar = uniform(0);
    // THE BODY LAYER LAW (see the header): a far nebula's depth is the far plane
    (vol.material as unknown as { vertexNode: N }).vertexNode = Fn((builder: N) => {
      const clip = cameraProjectionMatrix.mul(modelViewMatrix.mul(vec4(positionLocal, 1.0)));
      const r = (builder as { renderer?: { reversedDepthBuffer?: boolean; coordinateSystem?: number } } | undefined)?.renderer;
      if (r?.reversedDepthBuffer === true) return clip;
      const unit = clip.w.mul(DEPTH_CODE * (r?.coordinateSystem === THREE.WebGLCoordinateSystem ? 2 : 1));
      return vec4(clip.x, clip.y, select((uFar as N).greaterThan(0.5), min(clip.z.add(unit.mul(float(BODY_LAYER_CODES.behindFar))), clip.w), clip.z), clip.w);
    })();
    vol.setFade(0);
    vol.mesh.scale.setScalar(e.site.radius);
    vol.mesh.name = `nebula-volume:${e.site.key}`;
    const group = new THREE.Group();
    group.name = `nebula:${e.site.key}`;
    group.add(vol.mesh);
    e.vol = vol; e.group = group; e.uFar = uFar as unknown as { value: number }; e.stage = 'warming';
    void this.warm(e, group, this.gen);
  }

  /** THE WARM: compile the march in a private scene before its first draw, then seat it under the sector root. */
  private async warm(e: Entry, group: THREE.Group, gen: number): Promise<void> {
    const r = this.env.renderer() as { compileAsync?(s: THREE.Object3D, c: THREE.Camera, t?: THREE.Scene | null): Promise<void> } | null;
    const cam = this.env.camera();
    if (r?.compileAsync && cam) {
      const tmp = new THREE.Scene();
      tmp.add(group);
      try { await r.compileAsync(tmp, cam, this.env.scene()); } catch { /* the first draw compiles it instead */ }
      tmp.remove(group);
    }
    if (gen !== this.gen || e.group !== group) return;     // the sector moved on while it compiled (dispose took it)
    const parent = this.env.parent();
    if (!parent) { e.stage = 'failed'; e.error = 'no sector'; return; }
    parent.add(group);
    this.env.anchor(group, this._abs.set(e.site.pos[0], e.site.pos[1], e.site.pos[2]).clone());
    e.stage = 'shown';
  }

  /** Probes: finish every bake now (blocks) — a picture must not wait on idle frames. */
  finishNow(): void {
    for (let guard = 0; guard < 64 && this.entries.some((e) => e.stage === 'queued' || e.stage === 'baking'); guard++) {
      const e = this.entries.find((x) => x.stage === 'baking') ?? this.entries.find((x) => x.stage === 'queued');
      if (!e) break;
      if (e.stage === 'queued') { this.frame(0); continue; }
      e.baker!.finish();
      this.frame(0);
    }
  }
  /** Probes: skip the fade. */
  showNow(): void { for (const e of this.entries) if (e.stage === 'shown' && e.vol) { e.fade = 1; e.vol.setFade(1); } }
  /** Probes: the same view with and without the nebulae. */
  setVisible(on: boolean): void { for (const e of this.entries) if (e.group) e.group.visible = on; }

  dispose(): void {
    this.gen++;
    for (const e of this.entries) {
      if (e.group) { this.env.unanchor(e.group); e.group.removeFromParent(); }
      if (e.vol) { e.vol.mesh.removeFromParent(); disposeAfterSwap(e.vol); } // it leaves the scene NOW; its geometry, material and 3-D texture were freed in this same tick, under frames still in flight (the deferred-teardown law)
      e.vol = null; e.group = null; e.baker = null;
    }
    this.entries = [];
  }

  state(): NebulaControllerState {
    const ship = this.env.shipAbs(), q = NEBULA_QUALITY[this.level];
    const sites = this.entries.map((e) => {
      const d = Math.hypot(e.site.pos[0] - ship[0], e.site.pos[1] - ship[1], e.site.pos[2] - ship[2]);
      return { key: e.site.key, name: e.site.name, kind: e.site.kind, stage: e.stage, progress: e.stage === 'baking' ? e.baker?.progress ?? 0 : e.stage === 'queued' ? 0 : 1, distKm: d / 1000, far: d > NEBULA_FAR_M, fade: e.fade, bakeMs: Math.round(e.bakeMs), cached: e.cached, steps: e.vol?.steps ?? 0, error: e.error };
    });
    return { level: this.level, size: q.size, sites, shown: sites.filter((s) => s.stage === 'shown').length, pending: sites.filter((s) => s.stage === 'queued' || s.stage === 'baking' || s.stage === 'warming').length, cacheBytes: nebulaCacheBytes() };
  }
}
